import { FC, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import dayjs from 'dayjs';
import { OperationsBlockProps, SideModalLinkOperationStateType } from '../model/operations.types';

import { Flex } from '@chakra-ui-kraud/react';
import { SideModalLinkOperation } from '../side-modal-link-operation';

import { ModalOperation } from '../modal-operation';

import { CustomOperationsBlock } from '../custom-operations';
import { DefaultOperationsBlock } from '../default-operations';
import styles from './operations.module.scss';
import { FormT } from '@/pages/tech-card-page/formConfig';
import { useProductOperationsActions } from './operations-hooks';
import { useGetAllExecutorsQuery } from '@/shared/state/api/swagger';
import { selectExecutorsForOptions } from '@/entities/employee';

export const OperationsBlock: FC<OperationsBlockProps> = ({
	isEditable,
	focusedBlock,
	setFocusedBlock,
	historedOperations,
}) => {
	const { setValue, getValues } = useFormContext<FormT>();

	const {
		renderCancelButton,
		renderRenewOperationButton,
		cancelOperationInfo,
		setCancelOperationInfo,
		renewalOperation,
		setRenewalOperation,
	} = useProductOperationsActions();

	const { product_operations: fields } = getValues();
	const { default_product_operations: default_fields } = getValues();

	const [sideModalLinkOperation, setSideModalLinkOperation] = useState<SideModalLinkOperationStateType>({
		isOpen: false,
		title: '',
		operationId: null,
	});

	const { options: employee } = useGetAllExecutorsQuery(
		{ stringToSearch: '' },
		{
			selectFromResult: (result) => ({
				...result,
				options: selectExecutorsForOptions(result?.data),
			}),
		},
	);

	// отмена операции
	const handleCancelOperation = useCallback(() => {
		const checkIndex = () => {
			// поиск id операции в массиве дефолтных и обычных операций
			if (cancelOperationInfo.operationType === 'operation') {
				return fields?.findIndex((operation) => operation.id === cancelOperationInfo.operation?.id);
			} else {
				return default_fields?.findIndex((operation) => operation.id === cancelOperationInfo.operation?.id);
			}
		};

		// следующая неотмененная операция (обычная)
		const nextNotCanceledOperation = fields?.find(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);

		// индекс следующей неотмененной операции (обычной)
		const nextNotCanceledOperationIndex = fields?.findIndex(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);

		// следующая неотмененная операция (ДЕФОЛТНАЯ)
		const nextNotCanceledDefaultOperation = default_fields?.find(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);

		// индекс следующей неотмененной ДЕФОЛТНОЙ операции
		const nextNotCanceledDefaultOperationIndex = default_fields?.findIndex(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);

		// индекс текущей отменяемой операции
		const index = checkIndex() ?? -1;

		if (Number(index) > -1) {
			// если отменяем обычную операцию
			if (cancelOperationInfo.operationType === 'operation') {
				// отменяем операцию (меняем стейт)
				setValue(`product_operations.${index}.is_canceled`, true);
				// устанавливаем ФИО ответственного за отмену операции
				setValue(`product_operations.${index}.fio`, cancelOperationInfo.closingEmployeeFio);
				// устанавливаем дату отмены операции (либо выбранную в модалке, либо текущю дату)
				setValue(
					`product_operations.${index}.date`,
					cancelOperationInfo.closingDate ? cancelOperationInfo.closingDate : dayjs().toString(),
				);

				// если в отмененной операции были значения (грамм, штук) - переносим все значения в следующую неотмененную операцию
				if (
					index &&
					(cancelOperationInfo.operation?.count_in_gram ||
						cancelOperationInfo.operation?.count_in_number ||
						cancelOperationInfo.operation?.count_out_gram ||
						cancelOperationInfo.operation?.count_out_number ||
						cancelOperationInfo.operation?.wasted)
				) {
					// если это последняя обычная операция ИЛИ остальные (обычные) операции отменены (остались только дефолтные)
					if (
						((fields && fields.length - 1 === index) || nextNotCanceledOperationIndex === -1) &&
						nextNotCanceledDefaultOperationIndex !== undefined
					) {
						setValue(
							`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_in_gram`,
							cancelOperationInfo.operation?.count_in_gram,
						);
						setValue(
							`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_in_number`,
							cancelOperationInfo.operation?.count_in_number,
						);
						setValue(
							`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_out_gram`,
							cancelOperationInfo.operation?.count_out_gram,
						);
						setValue(
							`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_out_number`,
							cancelOperationInfo.operation?.count_out_number,
						);
						setValue(
							`default_product_operations.${nextNotCanceledDefaultOperationIndex}.wasted`,
							cancelOperationInfo.operation?.wasted,
						);
					} else {
						if (cancelOperationInfo.operation) {
							// если есть следующая неотмененная (обычныя) операция
							if (
								nextNotCanceledOperation &&
								nextNotCanceledOperationIndex !== -1 &&
								nextNotCanceledOperationIndex !== undefined
							) {
								// если у следующей неотмененной (обычной) операции нет значения (далее проверяем поля)
								!nextNotCanceledOperation.count_in_gram
									? setValue(
											`product_operations.${nextNotCanceledOperationIndex}.count_in_gram`,
											cancelOperationInfo.operation?.count_in_gram,
									  )
									: null;

								!nextNotCanceledOperation.count_in_number
									? setValue(
											`product_operations.${nextNotCanceledOperationIndex}.count_in_number`,
											cancelOperationInfo.operation?.count_in_number,
									  )
									: null;

								!nextNotCanceledOperation.count_out_gram
									? setValue(
											`product_operations.${nextNotCanceledOperationIndex}.count_out_gram`,
											cancelOperationInfo.operation?.count_out_gram,
									  )
									: null;

								!nextNotCanceledOperation.count_out_number
									? setValue(
											`product_operations.${nextNotCanceledOperationIndex}.count_out_number`,
											cancelOperationInfo.operation?.count_out_number,
									  )
									: null;

								!nextNotCanceledOperation.count_out_number
									? setValue(
											`product_operations.${nextNotCanceledOperationIndex}.wasted`,
											cancelOperationInfo.operation?.wasted,
									  )
									: null;
							}
						}
					}
				}

				// очищаем значения в текущей (отмененной) обычной операции
				setValue(`product_operations.${index}.count_in_gram`, undefined);
				setValue(`product_operations.${index}.count_in_number`, undefined);
				setValue(`product_operations.${index}.count_out_gram`, undefined);
				setValue(`product_operations.${index}.count_out_number`, undefined);
				setValue(`product_operations.${index}.wasted`, undefined);
			} else {
				// если отменяем ДЕФОЛТНУЮ операцию
				setValue(`default_product_operations.${index}.is_canceled`, true);
				setValue(`default_product_operations.${index}.fio`, cancelOperationInfo.closingEmployeeFio);
				setValue(
					`default_product_operations.${index}.date`,
					cancelOperationInfo.closingDate ? cancelOperationInfo.closingDate : dayjs().toString(),
				);
				if (cancelOperationInfo.operation) {
					// если есть следующая неотмененная (ДЕФОЛТНАЯ) операция
					if (
						nextNotCanceledDefaultOperation &&
						nextNotCanceledDefaultOperationIndex !== -1 &&
						nextNotCanceledDefaultOperationIndex !== undefined
					) {
						// если у следующей неотмененной (ДЕФОЛТНОЙ) операции нет значения (далее проверяем поля)
						!nextNotCanceledDefaultOperation.count_in_gram
							? setValue(
									`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_in_gram`,
									cancelOperationInfo.operation?.count_in_gram,
							  )
							: null;

						!nextNotCanceledDefaultOperation.count_in_number
							? setValue(
									`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_in_number`,
									cancelOperationInfo.operation?.count_in_number,
							  )
							: null;

						!nextNotCanceledDefaultOperation.count_out_gram
							? setValue(
									`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_out_gram`,
									cancelOperationInfo.operation?.count_out_gram,
							  )
							: null;

						!nextNotCanceledDefaultOperation.count_out_number
							? setValue(
									`default_product_operations.${nextNotCanceledDefaultOperationIndex}.count_out_number`,
									cancelOperationInfo.operation?.count_out_number,
							  )
							: null;

						!nextNotCanceledDefaultOperation.count_out_number
							? setValue(
									`default_product_operations.${nextNotCanceledDefaultOperationIndex}.wasted`,
									cancelOperationInfo.operation?.wasted,
							  )
							: null;
					}
				}

				// очищаем значения в текущей (отмененной) ДЕФОЛТНОЙ операции
				setValue(`default_product_operations.${index}.count_in_gram`, undefined);
				setValue(`default_product_operations.${index}.count_in_number`, undefined);
				setValue(`default_product_operations.${index}.count_out_gram`, undefined);
				setValue(`default_product_operations.${index}.count_out_number`, undefined);
				setValue(`default_product_operations.${index}.wasted`, undefined);
			}
		}

		// очищаем стейт отмененной операции
		setCancelOperationInfo({
			closingEmployeeFio: '',
			operation: undefined,
			operationType: undefined,
			isModalOpen: false,
			closingDate: '',
		});
	}, [cancelOperationInfo, fields, default_fields]);

	// возобновление операции
	const handleRenewalOperation = useCallback(() => {
		// поиск id операции в массиве дефолтных и обычных операций
		const checkIndex = () => {
			if (renewalOperation.operationType === 'operation') {
				return fields?.findIndex((operation) => operation.id === renewalOperation.operation?.id);
			} else {
				return default_fields?.findIndex((operation) => operation.id === renewalOperation.operation?.id);
			}
		};

		const index = checkIndex() ?? -1;

		if (Number(index) > -1) {
			if (renewalOperation.operationType === 'operation') {
				setValue(`product_operations.${index}.is_canceled`, false);
				setValue(`product_operations.${index}.fio`, '');
			} else {
				setValue(`default_product_operations.${index}.is_canceled`, false);
				setValue(`default_product_operations.${index}.fio`, '');
			}
		}

		// очищаем стейт возобновленной операции
		setRenewalOperation({
			operation: undefined,
			operationType: undefined,
			isModalOpen: false,
		});
	}, [
		setRenewalOperation,
		renewalOperation.operationType,
		renewalOperation.operation?.id,
		fields,
		default_fields,
		setValue,
	]);

	return (
		<>
			{sideModalLinkOperation.operationId && (
				<SideModalLinkOperation
					isOpen={sideModalLinkOperation.isOpen}
					onClose={() => setSideModalLinkOperation({ isOpen: false, title: '', operationId: null })}
					title={sideModalLinkOperation.title}
					operationId={sideModalLinkOperation.operationId}
				/>
			)}
			{cancelOperationInfo.isModalOpen && (
				<ModalOperation
					data={cancelOperationInfo}
					onSubmit={handleCancelOperation}
					variant="cancel"
					employee={employee}
					setData={setCancelOperationInfo}
				/>
			)}
			{renewalOperation.isModalOpen && (
				<ModalOperation
					data={renewalOperation}
					onSubmit={handleRenewalOperation}
					variant="renewal"
					setData={setRenewalOperation}
				/>
			)}

			<Flex className={styles['operations']} direction="column">
				<CustomOperationsBlock
					setSideModalLinkOperation={setSideModalLinkOperation}
					renderCancelButton={renderCancelButton}
					renderRenewOperationButton={renderRenewOperationButton}
					employee={employee}
					isEditable={isEditable}
					focusedBlock={focusedBlock}
					setFocusedBlock={setFocusedBlock}
					historedOperations={historedOperations}
				/>

				<DefaultOperationsBlock
					setSideModalLinkOperation={setSideModalLinkOperation}
					renderCancelButton={renderCancelButton}
					renderRenewOperationButton={renderRenewOperationButton}
					employee={employee}
					isEditable={isEditable}
					focusedBlock={focusedBlock}
					setFocusedBlock={setFocusedBlock}
					historedOperations={historedOperations}
				/>
			</Flex>
		</>
	);
};
